import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { useMemo, useState } from 'react'
import { useActiveOrganization } from 'hooks/use-organization'
import { toastAtom } from 'state'
import { useSetRecoilState } from 'recoil'
import SearchIcon from '@mui/icons-material/Search'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import MobileOverlay from 'components/MobileOverlay'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { ReadRichText } from 'components/RichEditor/RichEditor'
import { useFetchTwinPrompts } from 'hooks/use-prompts'
import { TwinPrompt } from 'api/model'
import AddOrEditTwinPromptModal from './AddOrEditPromptModal'
import { twinsDeletePrompt, twinsResetPrompts } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { getColorForDarkMode } from 'utils'
import { COLORS } from 'app-constants'

const TwinPromptsList = ({
  twinPrompts,
  twinPromptId,
  setTwinPromptId,
  setTwinPrompts,
}: {
  twinPrompts: TwinPrompt[]
  twinPromptId: string
  setTwinPromptId: (twinPromptId: string) => void
  setTwinPrompts: React.Dispatch<React.SetStateAction<TwinPrompt[]>>
}) => {
  const theme = useTheme()
  const isMobile = useCheckMobileScreen()

  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)

  const [isDeletingTwinPrompt, setIsDeletingTwinPrompt] = useState<boolean>(false)

  const [pendingDeleteTwinPromptId, setPendingDeleteTwinPromptId] = useState<string>('')

  const [searchText, setSearchText] = useState<string>('')
  const onSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  // filter list of prompts based on search text match against the prompt name
  const filteredTwinPrompts = useMemo(() => {
    return twinPrompts.filter((n) => {
      return n.name.toLowerCase().includes(searchText.toLowerCase())
    })
  }, [twinPrompts, searchText])

  const onDeletePromptClick = (event: React.SyntheticEvent, promptId: string) => {
    setPendingDeleteTwinPromptId(promptId)
    event.stopPropagation()
  }

  const onDeletePromptConfirmed = async () => {
    setIsDeletingTwinPrompt(true)

    if (!activeOrganization) {
      return
    }

    try {
      await twinsDeletePrompt(activeOrganization, pendingDeleteTwinPromptId)
      setTwinPrompts((prevPrompts: TwinPrompt[]) => prevPrompts.filter((n) => n.id !== pendingDeleteTwinPromptId))
      setToast({ title: 'Successfully deleted twin prompt', severity: 'success' })
    } catch (error) {
      setToast({ title: 'Prompt deletion failed', severity: 'error' })
    } finally {
      setIsDeletingTwinPrompt(false)
      setPendingDeleteTwinPromptId('')
    }
  }

  return (
    <Stack width={isMobile ? '100%' : '260px'} spacing={1}>
      <TextField
        placeholder="Search prompts"
        value={searchText}
        onChange={onSearchTextChange}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                sx={{
                  color: COLORS.GRAY_3,
                  marginRight: '-10px',
                }}
              />
            </InputAdornment>
          ),
        }}
        sx={{
          marginBottom: isMobile ? '16px' : '32px',
          width: isMobile ? '100%' : '260px',
        }}
      />

      {filteredTwinPrompts.map((p) => {
        return (
          <Stack
            key={p.id}
            borderRadius="8px"
            px={2}
            py={1}
            direction="row"
            alignItems="center"
            spacing={1}
            onClick={() => setTwinPromptId(p.id)}
            sx={{
              backgroundColor: twinPromptId === p.id ? COLORS.JELLED_MID_BLUE_10_PERCENT : 'transparent',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: COLORS.JELLED_MID_BLUE_10_PERCENT,
              },
            }}>
            <Stack width={180}>
              <Typography
                variant="h6"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                {p.name}
              </Typography>
              <Typography
                variant="pS"
                color="#666666"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                {p.description}
              </Typography>
            </Stack>

            <IconButton onClick={(e) => onDeletePromptClick(e, p.id)}>
              <DeleteOutlinedIcon sx={{ fill: COLORS.GRAY_0 }} />
            </IconButton>
          </Stack>
        )
      })}

      <Dialog open={!!pendingDeleteTwinPromptId}>
        <DialogTitle>Are you sure you want to delete this prompt?</DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              setPendingDeleteTwinPromptId('')
            }}
            disabled={isDeletingTwinPrompt}
            sx={{
              color: getColorForDarkMode(theme),
            }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={onDeletePromptConfirmed} disabled={isDeletingTwinPrompt}>
            Yes, delete it
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

const TwinPromptContent = ({
  twinPromptId,
  twinPrompts,
  onAddTwinPrompt,
  onEditTwinPrompt,
  isAddOrEditTwinPromptModalOpen,
  setIsAddOrEditTwinPromptModalOpen,
}: {
  twinPromptId: string
  twinPrompts: TwinPrompt[]
  onAddTwinPrompt: (twinPrompt: TwinPrompt) => void
  onEditTwinPrompt: (twinPrompt: TwinPrompt) => void
  isAddOrEditTwinPromptModalOpen: boolean
  setIsAddOrEditTwinPromptModalOpen: (isOpen: boolean) => void
}) => {
  const twinPrompt = twinPrompts.find((p) => p.id === twinPromptId)

  const isMobile = useCheckMobileScreen()

  if (twinPrompts.length === 0) {
    return (
      <Stack>
        <Typography variant="h4">No twin prompts yet</Typography>
      </Stack>
    )
  }

  if (!twinPromptId || !twinPrompt) {
    return (
      <Stack>
        <Typography>No twin prompt selected yet</Typography>
      </Stack>
    )
  }

  const onEditTwinPromptClick = () => {
    setIsAddOrEditTwinPromptModalOpen(true)
  }

  return (
    <Stack width="100%">
      <Stack
        direction={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'flex-start' : 'center'}
        justifyContent="space-between"
        mb={2}>
        <Typography variant="h4" mb={isMobile ? 1 : 0}>
          {twinPrompt.name}
        </Typography>

        <Button
          variant="contained"
          color="primary"
          onClick={onEditTwinPromptClick}
          startIcon={<EditOutlinedIcon />}
          sx={{
            width: 'fit-content',
            paddingTop: '0.5rem',
            paddingBottom: '0.5rem',
          }}>
          Edit twin prompt
        </Button>
      </Stack>

      <ReadRichText disableMaxHeight={true} value={twinPrompt.content} />
    </Stack>
  )
}

// administrative feature within for us
const TwinPrompts = () => {
  const isMobile = useCheckMobileScreen()

  const [isAddTwinPromptModalOpen, setIsAddTwinPromptModalOpen] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  const { isLoading, twinPrompts, setTwinPrompts } = useFetchTwinPrompts(reloadKey)

  const [twinPromptId, setTwinPromptId] = useState<string>('')

  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)

  const onAddTwinPrompt = (twinPrompt: TwinPrompt) => {
    setTwinPrompts((prevTwinPrompts: TwinPrompt[]) => [...prevTwinPrompts, twinPrompt])
    setTwinPromptId(twinPrompt.id)
  }

  const onEditTwinPrompt = (twinPrompt: TwinPrompt) => {
    setIsAddTwinPromptModalOpen(true)
    setTwinPrompts((prevTwinPrompts: TwinPrompt[]) => {
      const index = prevTwinPrompts.findIndex((p) => p.id === twinPrompt.id)
      const newTwinPrompts = [...prevTwinPrompts]
      newTwinPrompts[index] = twinPrompt
      return newTwinPrompts
    })
  }

  const onResetTwinPrompts = async () => {
    if (!activeOrganization) {
      return
    }
    try {
      await twinsResetPrompts(activeOrganization)
      setToast({ title: 'Successfully reset twin prompts', severity: 'success' })
    } catch (error) {
      setToast({ title: 'Prompt reset failed', severity: 'error' })
    } finally {
    }
    setReloadKey((prevKey) => prevKey + 1)
  }

  // TODO: add loading state
  if (isLoading) {
    return null
  }

  return (
    <Stack>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isMobile ? 'flex-start' : 'center'}
        mb={2}>
        {!isMobile && (
          <Typography variant="h4" mb={1}>
            Prompts
          </Typography>
        )}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              setTwinPromptId('')
              setIsAddTwinPromptModalOpen(true)
            }}>
            Add prompt
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              onResetTwinPrompts()
            }}>
            Reset prompts
          </Button>
        </Stack>
      </Stack>
      {isMobile ? (
        <Stack>
          <TwinPromptsList
            setTwinPrompts={setTwinPrompts}
            twinPrompts={twinPrompts}
            twinPromptId={twinPromptId}
            setTwinPromptId={setTwinPromptId}
          />
          {twinPromptId && (
            <MobileOverlay closeButtonColor="#222222" onClose={() => setTwinPromptId('')}>
              <TwinPromptContent
                twinPrompts={twinPrompts}
                twinPromptId={twinPromptId}
                onAddTwinPrompt={onAddTwinPrompt}
                onEditTwinPrompt={onEditTwinPrompt}
                isAddOrEditTwinPromptModalOpen={isAddTwinPromptModalOpen}
                setIsAddOrEditTwinPromptModalOpen={setIsAddTwinPromptModalOpen}
              />
            </MobileOverlay>
          )}
        </Stack>
      ) : (
        <>
          <Stack direction="row" spacing={4}>
            <TwinPromptsList
              setTwinPrompts={setTwinPrompts}
              twinPrompts={twinPrompts}
              twinPromptId={twinPromptId}
              setTwinPromptId={setTwinPromptId}
            />
            <TwinPromptContent
              twinPrompts={twinPrompts}
              twinPromptId={twinPromptId}
              onAddTwinPrompt={onAddTwinPrompt}
              onEditTwinPrompt={onEditTwinPrompt}
              isAddOrEditTwinPromptModalOpen={isAddTwinPromptModalOpen}
              setIsAddOrEditTwinPromptModalOpen={setIsAddTwinPromptModalOpen}
            />
          </Stack>
        </>
      )}

      {isAddTwinPromptModalOpen && (
        <AddOrEditTwinPromptModal
          onAddTwinPrompt={onAddTwinPrompt}
          onEditTwinPrompt={onEditTwinPrompt}
          twinPrompt={twinPrompts.find((p) => p.id === twinPromptId)}
          onClose={() => setIsAddTwinPromptModalOpen(false)}
        />
      )}
    </Stack>
  )
}

export default TwinPrompts
